/**
 * SubscriptionBlockOverlay
 *
 * Full-width overlay that **replaces** the composer area when the user
 * has been hard-blocked by the subscription enforcement system.
 *
 * Displayed when `backendState.subscription_blocked === true`.
 * The block_type values come from the backend (SubscriptionEnforcementService):
 *   - 'trial_limit'  → (legacy) trial users are now unlimited; only fires for post-trial edge cases
 *   - 'weekly_limit'  → paid user (full/extended) exhausted weekly chat limit
 *   - 'hard_block'    → post-trial user in 'blocked' phase (no active subscription)
 *   - (fallback)      → generic limit-reached message
 *
 * Sprint 7 – Task 7.8d
 */
import React from 'react';
import { CoachCedarColors } from '../../utils/styleConverter';

interface SubscriptionBlockOverlayProps {
  blockType?: string;   // 'trial_limit' | 'weekly_limit' | 'hard_block'
  tier?: string;        // current tier at time of block
  onUpgradeClick: () => void;
}

export const SubscriptionBlockOverlay: React.FC<SubscriptionBlockOverlayProps> = ({
  blockType,
  tier,
  onUpgradeClick,
}) => {
  /* ── copy / CTA per block reason ── */
  let headline: string;
  let body: string;
  let ctaLabel: string;

  switch (blockType) {
    case 'trial_limit':
      headline = "You've used all your trial messages";
      body =
        'Your trial includes 30 messages per week. Subscribe to unlock unlimited coaching with Cedar.';
      ctaLabel = 'See Plans';
      break;

    case 'hard_block':
      headline = 'Your free messages are all used up';
      body =
        'Cedar still has your goals, plans, and everything learned about you. Subscribe to pick up right where you left off.';
      ctaLabel = 'See Plans';
      break;

    case 'weekly_limit':
      headline = "You've reached your weekly limit";
      body =
        tier === 'extended'
          ? 'Your 60 weekly messages have been used. Your limit resets on your subscription anniversary.'
          : 'Your 30 weekly messages have been used. Upgrade to Extended for 60 messages per week, or wait for your weekly reset.';
      ctaLabel = tier === 'extended' ? 'Got It' : 'Upgrade';
      break;

    default:
      headline = 'Message limit reached';
      body = 'Upgrade your plan to continue chatting with Cedar.';
      ctaLabel = 'Upgrade';
  }

  /* ── styles ── */
  const overlay: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '20px 24px',
    background: 'linear-gradient(180deg, rgba(46,94,52,0.06) 0%, rgba(46,94,52,0.02) 100%)',
    borderTop: '1px solid rgba(46,94,52,0.15)',
    textAlign: 'center' as const,
  };

  const headlineStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: '#1a1a1a',
    letterSpacing: '-0.01em',
  };

  const bodyStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 13,
    lineHeight: '1.45',
    color: '#666',
    maxWidth: 340,
  };

  const ctaStyle: React.CSSProperties = {
    padding: '10px 28px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    background: CoachCedarColors.cedarPrime,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  };

  return (
    <div style={overlay}>
      <p style={headlineStyle}>{headline}</p>
      <p style={bodyStyle}>{body}</p>
      <button
        style={ctaStyle}
        onClick={onUpgradeClick}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = '#245029';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = CoachCedarColors.cedarPrime;
        }}
      >
        {ctaLabel}
      </button>
    </div>
  );
};

export default SubscriptionBlockOverlay;
